import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Heading, Spinner } from '@design-systems/canvas-ui'

const sessionFilterOptions = [
  { id: 'ACTIVE', label: 'Active', value: 'ACTIVE' },
  { id: 'ARCHIVED', label: 'Archived', value: 'ARCHIVED' },
]

const getSessionTitle = (session) =>
  session.title || session.brand || 'New conversation'

const ConversationSidebar = ({
  error,
  isLoading,
  listStatus,
  onArchiveSession,
  onDeleteSession,
  onLoadMore,
  onNewChat,
  onOpenSession,
  onRenameSession,
  onStatusChange,
  selectedSessionId,
  sessionCursor,
  sessions,
}) => {
  const [activeMenuSessionId, setActiveMenuSessionId] = useState(null)
  const [activeMenuPosition, setActiveMenuPosition] = useState(null)
  const sidebarRef = useRef(null)
  const sessionListRef = useRef(null)
  const loadingMoreRef = useRef(false)

  const updateActiveMenuPosition = useCallback(() => {
    if (!activeMenuSessionId || !sessionListRef.current) return

    const trigger = sessionListRef.current.querySelector(
      `[data-session-menu-trigger="${activeMenuSessionId}"]`,
    )

    if (!trigger) return

    const triggerRect = trigger.getBoundingClientRect()
    const menuWidth = 150
    const menuHeight = 170
    const left = Math.min(
      Math.max(triggerRect.right - menuWidth, 12),
      window.innerWidth - menuWidth - 12,
    )
    const top = Math.min(
      triggerRect.bottom + 8,
      window.innerHeight - menuHeight - 8,
    )

    setActiveMenuPosition({ left, top })
  }, [activeMenuSessionId])

  useEffect(() => {
    if (!activeMenuSessionId) return undefined

    updateActiveMenuPosition()

    const list = sessionListRef.current
    if (!list) return undefined

    list.addEventListener('scroll', updateActiveMenuPosition, { passive: true })
    window.addEventListener('resize', updateActiveMenuPosition)

    return () => {
      list.removeEventListener('scroll', updateActiveMenuPosition)
      window.removeEventListener('resize', updateActiveMenuPosition)
    }
  }, [activeMenuSessionId, updateActiveMenuPosition])

  const loadMoreSessions = useCallback(async () => {
    if (!sessionCursor || isLoading || loadingMoreRef.current) return
    loadingMoreRef.current = true
    try {
      await onLoadMore()
    } finally {
      loadingMoreRef.current = false
    }
  }, [isLoading, onLoadMore, sessionCursor])

  useEffect(() => {
    const list = sessionListRef.current
    if (!list) return undefined

    const loadIfListDoesNotOverflow = () => {
      if (list.scrollHeight <= list.clientHeight + 1) loadMoreSessions()
    }

    loadIfListDoesNotOverflow()
    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(loadIfListDoesNotOverflow)
    observer.observe(list)
    return () => observer.disconnect()
  }, [loadMoreSessions, sessions.length])

  useEffect(() => {
    const handlePointerDown = (event) => {
      const target = event.target
      const clickedMenu = target.closest('.assistant__session-menu')
      const clickedTrigger = target.closest('.assistant__session-menu-trigger')

      if (!clickedMenu && !clickedTrigger && sidebarRef.current) {
        setActiveMenuSessionId(null)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  return (
    <aside
      className="assistant__sidebar"
      aria-label="Conversation history"
      ref={sidebarRef}
    >
      <div className="assistant__sidebar-top">
        <div>
          <p className="assistant__eyebrow">BrandDNA</p>
          <Heading as="h1" size={3}>
            Assistant
          </Heading>
        </div>
        <Button onClick={onNewChat} size="sm" variant="primary">
          <span aria-hidden="true">＋</span> New chat
        </Button>
      </div>
      <div
        aria-label="Conversation history status"
        className="assistant__status-toggle"
      >
        {sessionFilterOptions.map((option) => (
          <button
            aria-pressed={listStatus === option.value}
            className={
              listStatus === option.value
                ? 'assistant__status-pill assistant__status-pill--active'
                : 'assistant__status-pill'
            }
            key={option.value}
            onClick={() => onStatusChange(option.value)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
      {error && (
        <p className="assistant__sidebar-error" role="alert">
          {error}
        </p>
      )}
      <div
        className="assistant__session-list"
        ref={sessionListRef}
        onScroll={(event) => {
          const { clientHeight, scrollHeight, scrollTop } = event.currentTarget
          if (
            sessionCursor &&
            !isLoading &&
            !loadingMoreRef.current &&
            scrollHeight - scrollTop - clientHeight < 80
          ) {
            loadMoreSessions()
          }
        }}
      >
        {isLoading && !sessions.length ? (
          <div className="assistant__loading-sessions" role="status">
            <Spinner size="sm" />
            <span>Loading conversations…</span>
          </div>
        ) : sessions.length ? (
          sessions.map((session) => (
            <div className="assistant__session-item" key={session.session_id}>
              <Button
                aria-current={
                  selectedSessionId === session.session_id ? 'page' : undefined
                }
                className="assistant__session"
                isFullWidth
                onClick={() => onOpenSession(session.session_id)}
                size="sm"
                type="button"
                variant="ghost-neutral"
              >
                <span className="assistant__session-copy">
                  <strong>{getSessionTitle(session)}</strong>
                  <span>{session.exchange_count || 0} questions</span>
                </span>
              </Button>
              <div className="assistant__session-menu-wrap">
                <button
                  aria-label={`More actions for ${getSessionTitle(session)}`}
                  className="assistant__session-menu-trigger"
                  data-session-menu-trigger={session.session_id}
                  onClick={(event) => {
                    event.stopPropagation()
                    setActiveMenuSessionId((current) =>
                      current === session.session_id
                        ? null
                        : session.session_id,
                    )
                  }}
                  type="button"
                >
                  ⋮
                </button>
                {activeMenuSessionId === session.session_id && (
                  <div
                    className="assistant__session-menu"
                    role="menu"
                    style={{
                      left: `${activeMenuPosition?.left ?? 0}px`,
                      top: `${activeMenuPosition?.top ?? 0}px`,
                    }}
                  >
                    <button
                      onClick={(event) => {
                        event.stopPropagation()
                        setActiveMenuSessionId(null)
                        setActiveMenuPosition(null)
                        onOpenSession(session.session_id)
                        onRenameSession?.(session)
                      }}
                      role="menuitem"
                      type="button"
                    >
                      Rename
                    </button>
                    <button
                      onClick={(event) => {
                        event.stopPropagation()
                        setActiveMenuSessionId(null)
                        setActiveMenuPosition(null)
                        onArchiveSession?.(session)
                      }}
                      role="menuitem"
                      type="button"
                    >
                      {session.status === 'ARCHIVED' ? 'Restore' : 'Archive'}
                    </button>
                    <button
                      onClick={(event) => {
                        event.stopPropagation()
                        setActiveMenuSessionId(null)
                        setActiveMenuPosition(null)
                        onOpenSession(session.session_id)
                        onDeleteSession?.(session)
                      }}
                      role="menuitem"
                      type="button"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <p className="assistant__muted">
            Your conversations will appear here.
          </p>
        )}
        {isLoading && sessions.length > 0 && (
          <div className="assistant__loading-sessions" role="status">
            <Spinner size="sm" />
            <span>Loading more conversations…</span>
          </div>
        )}
      </div>
      {!isLoading && sessionCursor && (
        <p className="assistant__scroll-hint">Scroll for more conversations</p>
      )}
      <div className="assistant__sidebar-note">
        <span aria-hidden="true">✦</span>
        <p>
          Explore brand guidance across Target brands, or choose a brand to keep
          the conversation focused.
        </p>
      </div>
    </aside>
  )
}

export default ConversationSidebar
